import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function AdminLogin() {
  const [form, setForm] = useState({ username: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Use the API service which respects environment configuration
      const response = await api.adminRequest('/admin/login/', {
        method: 'POST',
        body: JSON.stringify({
          email: form.username,
          password: form.password
        })
      });

      if (response.success) {
        // Store admin auth token and complete auth data
        localStorage.setItem("adminAuthToken", response.data.access_token);
        localStorage.setItem("adminRefreshToken", response.data.refresh_token);
        localStorage.setItem("adminAuth", JSON.stringify(response.data));
        localStorage.setItem("adminData", JSON.stringify(response.data.user)); // Changed from adminUser to adminData
        navigate("/admin/dashboard");
      } else {
        setError(response.error || "Invalid admin credentials.");
      }
    } catch (error) {
      // Don't log expected authentication failures to console
      // Invalid credentials is a normal user error, not a system error
      // Only log unexpected errors
      if (error.message && !error.message.includes('Invalid') && !error.message.includes('Admin login failed')) {
        console.error('Unexpected admin login error:', error);
      }
      
      // Show user-friendly error message
      if (error.message && (error.message.includes('Invalid') || error.message.includes('Admin login failed'))) {
        setError("Invalid admin credentials.");
      } else {
        setError("Login failed. Please try again.");
      }
    }
    
    setLoading(false);
  }

  return (
    <div style={styles.bg}>
      <div style={styles.loginBox}>
        <Logo />
        <h2 style={styles.heading}>Admin Login</h2>
        <form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", flexDirection: "column", gap: 22 }}>
          <input
            style={styles.input}
            autoFocus
            type="text"
            placeholder="Admin Username"
            value={form.username}
            onChange={e => setForm({ ...form, username: e.target.value })}
            required
            autoComplete="username"
          />
          <div style={{ position: "relative" }}>
            <input
              style={styles.input}
              type={showPw ? "text" : "password"}
              placeholder="Password"
              autoComplete="current-password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label={showPw ? "Hide password" : "Show password"}
              style={styles.togglePw}
              onClick={() => setShowPw((s) => !s)}
            >
              {showPw ? <AiOutlineClose /> : <AiOutlineEdit />} {/* swap for eye/eye-off */}
            </button>
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button disabled={loading} type="submit" style={styles.submit}>
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
        {/* <div style={styles.hint}>
          <span style={{ fontWeight: 500 }}>Demo credentials:</span> <br />
          <code>admin | Admin@2025</code>
        </div> */}
        <div style={styles.smalltext}>© 2025 Nexen Corporate</div>
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div style={{ marginBottom: 14, marginTop: -10, textAlign: "center" }}>
      {/* Customize as your actual company needs */}
      <svg width="56" height="56" fill="none" viewBox="0 0 90 90" style={{ filter: "drop-shadow(0 2px 12px #9bc9fa)" }}>
        <rect width="90" height="90" rx="21" fill="url(#bg)" />
        <text x="49%" y="57%" textAnchor="middle" fill="#fff" fontSize="34" fontWeight="900" fontFamily="monospace">
          N
        </text>
        <defs>
          <linearGradient id="bg" x1="0" y1="0" x2="90" y2="90" gradientUnits="userSpaceOnUse">
            <stop stopColor="#38b6ff" />
            <stop offset="1" stopColor="#6659fd" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

import { AiOutlineEdit, AiOutlineClose } from "react-icons/ai";

const styles = {
  bg: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#e6efff 0%,#c1eafe 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Inter', 'Poppins', sans-serif",
  },
  loginBox: {
    background: "rgba(255,255,255,0.98)",
    borderRadius: 22,
    padding: "42px 36px 38px 36px",
    boxShadow: "0 10px 44px #82aaf922",
    minWidth: 335,
    maxWidth: 360,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  heading: {
    margin: "10px 0 30px 0",
    color: "#215ea7",
    fontWeight: "900",
    fontSize: 26,
    letterSpacing: 0.02,
  },
  input: {
    width: "100%",
    padding: "13px 16px",
    fontSize: 17,
    borderRadius: 9,
    border: "1.4px solid #c0daf9",
    outline: "#6659fd",
    marginBottom: 2,
    background: "rgba(230,252,255,0.09)",
    fontWeight: 500,
    transition: ".13s border",
    boxShadow: "0 0px 0.5px #b2e5fd40",
  },
  togglePw: {
    position: "absolute",
    right: 12,
    top: "52%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    fontSize: 22,
    color: "#479fd4",
    cursor: "pointer",
  },
  submit: {
    fontWeight: 700,
    fontSize: 18,
    marginTop: 10,
    letterSpacing: 0.01,
    background: "linear-gradient(90deg,#2351e3,#3bc0fd 60%)",
    border: "none",
    padding: "12px 0",
    color: "#fff",
    borderRadius: 15,
    boxShadow: "0 4px 18px #a3d3fa22",
    cursor: "pointer",
    transition: ".12s background",
  },
  error: {
    fontWeight: 600,
    color: "#e95959",
    marginBottom: -5,
    marginTop: "-12px",
    textAlign: "left",
    fontSize: 15,
  },
  hint: {
    marginTop: 35,
    color: "#aaa",
    background: "#eff7fb",
    borderRadius: 8,
    fontSize: 14,
    padding: "8px 14px 4px 14px",
    textAlign: "center",
    marginBottom: 8,
  },
  smalltext: {
    fontSize: 11,
    color: "#bacadb",
    marginTop: 18,
    textAlign: "center",
    letterSpacing: 0.06,
  },
};
